import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr, ROLE_LABELS, isInternal } from "@/lib/api";
import { fmtDate } from "@/components/DateInput";
import { StatusBadge } from "@/components/StatusBadge";
import { AttachmentPreview } from "@/components/AttachmentPreview";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { fl } from "@/lib/finI18n";
import { TxnJournal } from "@/components/txn/TxnJournal";
import { tx, dateLocale, actLabel } from "@/lib/txnI18n";
import { coaName } from "@/lib/masterI18n";
import { fmtRp } from "@/components/AmountInput";
import {
  Paperclip, Upload, Send, CheckCircle2, RotateCcw, Archive, MessageSquare,
  FileText, Download, History, Trash2, AlertTriangle, Eye,
} from "lucide-react";

const ACTIONS = {
  draft: [{ to: "review", label: "sendWorkflow", icon: Send, variant: "default", roles: ["client_staff", "client_backup", "super_admin"] }],
  review: [
    { to: "checked", label: "checkL1", icon: CheckCircle2, variant: "default", roles: ["staff_keuangan", "staff_pajak", "leader", "supervisor", "super_admin"] },
    { to: "revisi", label: "askRevision", icon: RotateCcw, variant: "outline", roles: ["staff_keuangan", "staff_pajak", "leader", "supervisor", "super_admin"] },
  ],
  checked: [
    { to: "approved", label: "approveL2", icon: CheckCircle2, variant: "default", roles: ["leader", "supervisor", "super_admin"] },
    { to: "revisi", label: "askRevision", icon: RotateCcw, variant: "outline", roles: ["leader", "supervisor", "super_admin"] },
  ],
  revisi: [{ to: "review", label: "resubmit", icon: Send, variant: "default", roles: ["client_staff", "client_backup", "super_admin"] }],
  approved: [
    { to: "archived", label: "archive", icon: Archive, variant: "default", roles: ["supervisor", "super_admin"] },
    { to: "revisi", label: "reopen", icon: RotateCcw, variant: "outline", roles: ["leader", "supervisor", "super_admin"] },
  ],
  archived: [],
};

export function TransactionDetailPanel({ txnId, open, onOpenChange, onChanged, onEdit }) {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [txn, setTxn] = useState(null);
  const [comment, setComment] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    if (!txnId) return;
    try { const { data } = await api.get(`/transactions/${txnId}`); setTxn(data); } catch (e) { console.error("load transaction failed", e); }
  };
  useEffect(() => { if (open) load(); }, [txnId, open]);

  const changeStatus = async (to) => {
    try {
      await api.post(`/transactions/${txnId}/status`, { status: to, note });
      toast.success(tx("statusUpdated", lang));
      setNote("");
      load(); onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const addComment = async (type) => {
    const text = type === "revision_request" ? note : comment;
    if (!text.trim()) return;
    try {
      await api.post(`/transactions/${txnId}/comments`, { text, type });
      toast.success(tx(type === "revision_request" ? "revisionSent" : "commentAdded", lang));
      setComment(""); setNote(""); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/transactions/${txnId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(tx("uploaded", lang));
      load(); onChanged && onChanged();
    } catch (er) { toast.error(apiErr(er.response?.data?.detail)); }
    e.target.value = "";
  };

  const download = async (att) => {
    try {
      const res = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(tx("downloadFail", lang)); }
  };

  const viewAtt = (att) => setPreview(att);

  const remove = async () => {
    if (!window.confirm(tx("confirmDelete", lang))) return;
    try {
      const { data } = await api.delete(`/transactions/${txnId}`);
      toast.success(tx("deleted", lang));
      if (data?.recalculated) toast.info(`${data.recalculated} ${fl("recalcToast", lang)}`);
      onOpenChange(false); onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!txn) return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent><SheetHeader><SheetTitle className="sr-only">{tx("detail", lang)}</SheetTitle></SheetHeader></SheetContent>
    </Sheet>
  );

  const actions = (ACTIONS[txn.status] || []).filter((a) => a.roles.includes(user.role));
  const canEdit = (isInternal(user.role) && user.role !== "staff_pajak") ||
    (["client_staff", "client_backup"].includes(user.role) && ["draft", "revisi"].includes(txn.status));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl p-0 flex flex-col bg-card" data-testid="txn-detail-panel">
        <SheetHeader className="p-5 border-b border-border">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-head text-lg">{txn.invoice_no || tx("txn", lang)}</SheetTitle>
            <StatusBadge status={txn.status} />
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className={`num text-xl font-semibold ${txn.flow === "in" ? "text-emerald-600" : "text-rose-600"}`}>
              {txn.flow === "in" ? "+" : "−"}{fmtRp(txn.amount, lang)}
            </span>
            <span className="text-xs text-muted-foreground">{fmtDate(txn.date)}</span>
          </div>
          {txn.source === "invoice_payment" && (
            <div className="text-xs text-sky-700 dark:text-sky-300 mt-1" data-testid="txn-from-invoice">{tx("fromInvoice", lang)} {txn.invoice_no}</div>
          )}
          {txn.pending_document && (
            <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300 mt-1">
              <AlertTriangle className="h-3.5 w-3.5" /> Pending Document
            </div>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-auto">
          {/* meta */}
          <div className="p-5 grid grid-cols-2 gap-y-3 gap-x-4 text-sm border-b border-border">
            <Meta label={tx("vendor", lang)} value={txn.vendor || "—"} />
            <Meta label={tx("bank", lang)} value={txn.bank_name || "—"} />
            {txn.currency && txn.currency !== "IDR" ? <Meta label={tx("rate", lang)} value={`1 ${txn.currency} = Rp ${Number(txn.fx_rate || 0).toLocaleString("id-ID", { maximumFractionDigits: 4 })}${txn.kmk_no ? ` · KMK ${txn.kmk_no}` : " · manual"}`} /> : null}
            <Meta label={txn.kind === "contra" ? tx("transferTo", lang) : "COA"} value={txn.kind === "contra" ? (txn.to_bank_name || "—") : `${txn.coa_code || ""} ${coaName({ name: txn.coa_name, name_en: txn.coa_name_en, name_zh: txn.coa_name_zh }, lang) || ""}${txn.lines?.length > 1 ? ` (+${txn.lines.length - 1} ${tx("rows", lang)})` : ""}`} />
            {txn.kind === "contra" && txn.to_currency && txn.to_currency !== (txn.currency || "IDR") ? (
              <Meta label={tx("received", lang)} value={`${Number(txn.to_amount || 0).toLocaleString(dateLocale(lang), { maximumFractionDigits: 2 })} ${txn.to_currency} · ${tx(Number(txn.fx_diff || 0) >= 0 ? "gain" : "loss", lang)} ${fmtRp(Math.abs(txn.fx_diff || 0), lang)}`} />
            ) : null}
            <Meta label={tx("project", lang)} value={txn.project_name || "—"} />
            <Meta label={tx("tax", lang)} value={txn.tax_name ? `${txn.tax_name} (${txn.tax_rate}%)` : "—"} />
            {(txn.ppn_amount || txn.pph_amount) ? <Meta label="PPN / PPh" value={`${fmtRp(txn.ppn_amount || 0, lang)} / ${fmtRp(txn.pph_amount || 0, lang)}`} /> : null}
            {txn.total ? <Meta label={tx("totalTax", lang)} value={fmtRp(txn.total, lang)} /> : null}
            <Meta label={tx("createdBy", lang)} value={txn.created_by_name} />
            <div className="col-span-2"><Meta label={tx("desc", lang)} value={txn.description || "—"} /></div>
          </div>

          <TxnJournal txnId={txn.id} stamp={`${txn.status}-${txn.updated_at || ""}`} />

          {actions.length > 0 && (
            <div className="p-5 border-b border-border space-y-2">
              <Textarea placeholder={tx("statusNote", lang)} value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="status-note" />
              <div className="flex flex-wrap gap-2">
                {actions.map((a) => (
                  <Button key={a.to} size="sm" variant={a.variant} onClick={() => changeStatus(a.to)} data-testid={`action-${a.to}`}>
                    <a.icon className="h-4 w-4 mr-1.5" /> {tx(a.label, lang)}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <Tabs defaultValue="attachments" className="p-5">
            <TabsList className="w-full grid grid-cols-3">
              <TabsTrigger value="attachments" data-testid="tab-attachments"><Paperclip className="h-3.5 w-3.5 mr-1" />{tx("attachments", lang)}</TabsTrigger>
              <TabsTrigger value="comments" data-testid="tab-comments"><MessageSquare className="h-3.5 w-3.5 mr-1" />{tx("discussion", lang)}</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history"><History className="h-3.5 w-3.5 mr-1" />{tx("history", lang)}</TabsTrigger>
            </TabsList>

            <TabsContent value="attachments" className="mt-4 space-y-2">
              <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid="attach-input"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv" />
              <Button variant="outline" size="sm" className="w-full" onClick={() => fileRef.current?.click()} data-testid="attach-btn">
                <Upload className="h-4 w-4 mr-2" /> {tx("uploadAtt", lang)}
              </Button>
              {txn.attachments?.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">{tx("noAtt", lang)}</p>}
              {txn.attachments?.map((a) => (
                <div key={a.id} className="flex items-center gap-2 rounded-md border border-border p-2 text-sm" data-testid="attach-row">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <button type="button" className="truncate flex-1 text-left hover:underline" onClick={() => viewAtt(a)} data-testid={`attach-open-${a.id}`}>{a.original_filename}</button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" title={tx("view", lang)} onClick={() => viewAtt(a)} data-testid={`attach-view-${a.id}`}><Eye className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" title={tx("download", lang)} onClick={() => download(a)} data-testid={`attach-download-${a.id}`}><Download className="h-4 w-4" /></Button>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="comments" className="mt-4 space-y-3">
              <div className="space-y-2 max-h-72 overflow-auto">
                {txn.comments?.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">{tx("noDisc", lang)}</p>}
                {txn.comments?.map((c) => (
                  <div key={c.id} className={`rounded-md border p-2.5 text-sm ${c.type === "revision_request" ? "border-rose-300 bg-rose-50 dark:bg-rose-950/40" : "border-border"}`} data-testid="comment-row">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold">{c.user_name} <span className="text-muted-foreground font-normal">· {ROLE_LABELS[c.role]}</span></span>
                      {c.type === "revision_request" && <span className="text-[10px] uppercase font-bold text-rose-600">{tx("revision", lang)}</span>}
                    </div>
                    <p className="mt-1 text-sm">{c.text}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{new Date(c.created_at).toLocaleString(dateLocale(lang))}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2 pt-2 border-t border-border">
                <Textarea placeholder={tx("writeComment", lang)} value={comment} onChange={(e) => setComment(e.target.value)} rows={2} data-testid="comment-input" />
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1" onClick={() => addComment("comment")} data-testid="comment-send">{tx("sendComment", lang)}</Button>
                  {isInternal(user.role) && (
                    <Button size="sm" variant="outline" onClick={() => { setNote(comment); addComment("revision_request"); }} data-testid="request-revision">
                      {tx("askRevision", lang)}
                    </Button>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="history" className="mt-4 space-y-2">
              {txn.history?.map((h) => (
                <div key={h.id} className="flex gap-2 text-xs" data-testid="history-row">
                  <div className="h-2 w-2 rounded-full bg-primary mt-1 shrink-0" />
                  <div>
                    <p className="font-medium">{actLabel(h.action, lang)} <span className="text-muted-foreground font-normal">{tx("by", lang)} {h.user_name}</span></p>
                    <p className="text-[10px] text-muted-foreground">{new Date(h.timestamp).toLocaleString(dateLocale(lang))}</p>
                  </div>
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </div>

        {canEdit && (
          <div className="p-4 border-t border-border flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => { onEdit && onEdit(txn); }} data-testid="edit-txn-btn">{tx("edit", lang)}</Button>
            {(user.role === "super_admin" || ["draft", "revisi"].includes(txn.status)) && (
              <Button variant="outline" size="icon" onClick={remove} data-testid="delete-txn-btn"><Trash2 className="h-4 w-4" /></Button>
            )}
          </div>
        )}
        <AttachmentPreview att={preview} onClose={() => setPreview(null)} />
      </SheetContent>
    </Sheet>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm mt-0.5 break-words">{value}</p>
    </div>
  );
}
